import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// ─── Osservabilità crash client ────────────────────────────────────────────────
// Finora un qualunque errore JS in fase di render mostrava una PAGINA BIANCA
// silenziosa, senza alcun log: impossibile capire il perché (es. il pulsante
// WhatsApp che apre /manager su un browser in-app datato). Qui:
//  1) inviamo l'errore all'endpoint già esistente POST /api/log_client_error/
//     così lo vediamo lato server anche senza accedere al telefono del negozio;
//  2) al posto del bianco mostriamo un fallback visibile con reload
//     cache-busting (utile anche se un proxy servisse una index.html stantia).
// URL ESATTA di atterraggio, catturata subito all'avvio PRIMA che il router o
// il pannello manager la ripuliscano (ManagerRequests fa history.replaceState e
// toglie il token dal path). Serve per capire se il pulsante WhatsApp consegna
// una URL rotta (token troncato, ':' codificato, bookingId mancante, ecc.).
const INITIAL_URL = window.location.href;

const postClientLog = (context: string, extra: any) => {
  try {
    const payload = {
      context,
      // URL corrente + quella originale di atterraggio (possono differire dopo
      // la pulizia dell'URL): così vediamo esattamente cosa ha aperto il device.
      url: window.location.href,
      initialUrl: INITIAL_URL,
      userAgent: navigator.userAgent,
      ...extra,
    };
    // keepalive: l'invio parte anche se la pagina si sta chiudendo/ricaricando.
    fetch('/api/log_client_error/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* mai far fallire il reporting */ });
  } catch (e) { /* ignore */ }
};

const reportClientError = (context: string, error: any) => {
  postClientLog(context, {
    error: (error && (error.stack || error.message)) ? String(error.stack || error.message) : String(error),
  });
};

// Beacon una-tantum all'apertura di /manager: logga la URL GREZZA di atterraggio
// ANCHE senza crash, così se il pulsante WhatsApp manda un link rotto (che non
// fa crashare ma mostra un manager vuoto) lo vediamo comunque nei log.
try {
  if (window.location.pathname.indexOf('/manager') !== -1) {
    postClientLog('manager_open', { landingUrl: INITIAL_URL });
  }
} catch (e) { /* ignore */ }

// Reload che aggira eventuali cache intermedie (browser/proxy) aggiungendo un
// parametro univoco: forza il ri-download della index.html.
const cacheBustingReload = () => {
  try {
    const sep = window.location.search ? '&' : '?';
    window.location.replace(window.location.pathname + window.location.search + sep + '_r=' + Date.now());
  } catch (e) {
    window.location.reload();
  }
};

// Errori JS non catturati (fuori da React) e promesse rifiutate.
window.addEventListener('error', (e) => reportClientError('window.onerror', e.error || e.message));
window.addEventListener('unhandledrejection', (e: any) => reportClientError('unhandledrejection', e && e.reason));

type EBProps = { children: React.ReactNode };
type EBState = { hasError: boolean };

class RootErrorBoundary extends React.Component<EBProps, EBState> {
  constructor(props: EBProps) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: any, info: any) {
    reportClientError('react_render', error && (error.stack || error.message) ? error : { message: String(error), stack: info && info.componentStack });
    // Tentativo di auto-riparazione: al PRIMO crash della sessione ricarichiamo
    // una volta sola con cache-busting. Se la causa era transitoria o una
    // index.html/asset stantio servito da una cache, il reload la risolve da
    // solo senza che il negozio debba fare nulla. Il guard in sessionStorage
    // evita qualsiasi loop: se dopo il reload ricrasha, mostriamo il fallback.
    try {
      const k = '__padbike_eb_retry__';
      if (!sessionStorage.getItem(k)) {
        sessionStorage.setItem(k, '1');
        cacheBustingReload();
      }
    } catch (e) { /* ignore */ }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          maxWidth: 480, margin: '0 auto', padding: '48px 24px', textAlign: 'center', color: '#1f2937',
        }}>
          <h2 style={{ fontSize: 20, marginBottom: 12 }}>Qualcosa è andato storto</h2>
          <p style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 24, color: '#4b5563' }}>
            Non siamo riusciti a caricare la pagina. Riprova a ricaricare.<br />
            Something went wrong. Please try reloading the page.
          </p>
          <button
            onClick={cacheBustingReload}
            style={{
              background: '#2563eb', color: '#fff', border: 'none', borderRadius: 24,
              padding: '12px 28px', fontSize: 15, cursor: 'pointer',
            }}
          >
            Ricarica / Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
